package com.yogaaicoach.pages.insights

import com.yogaaicoach.pages.insights.data.MonthlyChartPoint
import com.yogaaicoach.pages.insights.data.WeeklyChartPoint
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus

internal enum class ChartViewMode { Monthly, Weekly }

internal data class ChartBarData(
    val label: String,
    val minutes: Int,
    val tooltipLabel: String,
)

private val monthAbbr = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
private val monthFull = listOf("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December")

// Anchors to the newest data point, computes every calendar month slot, looks up in a map.
// Slots with no data show as 0 (gap preservation).
internal fun monthlyBars(data: List<MonthlyChartPoint>, offset: Int): List<ChartBarData> {
    if (data.isEmpty()) return List(12) { ChartBarData("", 0, "") }
    val dataMap = data.associateBy { it.period }
    val (newestYear, newestMonth) = data.first().period.split("-").let { it[0].toInt() to it[1].toInt() }

    return (0 until 12).map { i ->
        // i=0 is leftmost (oldest in page), i=11 is rightmost (newest in page)
        val monthsBack = offset * 12 + (11 - i)
        val totalMonthIndex = newestYear * 12 + (newestMonth - 1) - monthsBack
        val year = totalMonthIndex / 12
        val month = totalMonthIndex % 12 + 1
        val period = "$year-${month.toString().padStart(2, '0')}"
        ChartBarData(
            label = monthAbbr[month - 1],
            minutes = dataMap[period]?.minutes ?: 0,
            tooltipLabel = "${monthFull[month - 1]} $year",
        )
    }
}

// Anchors to the newest data point, computes every calendar week slot, looks up in a map.
// Slots with no data show as 0 (gap preservation).
internal fun weeklyBars(data: List<WeeklyChartPoint>, offset: Int): List<ChartBarData> {
    if (data.isEmpty()) return List(12) { ChartBarData("", 0, "") }
    val dataMap = data.associateBy { it.weekStart }
    val newestWeekEpoch = LocalDate.parse(data.first().weekStart).toEpochDays()

    return (0 until 12).map { i ->
        // i=0 is leftmost (oldest in page), i=11 is rightmost (newest in page)
        val weeksBack = offset * 12 + (11 - i)
        val weekStart = LocalDate.fromEpochDays(newestWeekEpoch - weeksBack * 7)
        val prevWeekStart = LocalDate.fromEpochDays(newestWeekEpoch - (weeksBack + 1) * 7)
        val weekEnd = weekStart.plus(6, DateTimeUnit.DAY)
        val label = if (i == 0 || weekStart.month.number != prevWeekStart.month.number) {
            monthAbbr[weekStart.month.number - 1]
        } else ""
        val tooltipLabel = "${monthAbbr[weekStart.month.number - 1]} ${weekStart.day} – ${monthAbbr[weekEnd.month.number - 1]} ${weekEnd.day}"
        ChartBarData(label = label, minutes = dataMap[weekStart.toString()]?.minutes ?: 0, tooltipLabel = tooltipLabel)
    }
}

internal fun periodLabel(
    mode: ChartViewMode,
    monthlyData: List<MonthlyChartPoint>,
    weeklyData: List<WeeklyChartPoint>,
    offset: Int,
): String {
    return when (mode) {
        ChartViewMode.Monthly -> {
            if (monthlyData.isEmpty()) return ""
            val (ny, nm) = monthlyData.first().period.split("-").let { it[0].toInt() to it[1].toInt() }
            val newestIndex = ny * 12 + (nm - 1) - offset * 12
            val oldestIndex = newestIndex - 11
            val newestYear = newestIndex / 12; val newestMonth = newestIndex % 12 + 1
            val oldestYear = oldestIndex / 12; val oldestMonth = oldestIndex % 12 + 1
            if (oldestYear == newestYear) "${monthAbbr[oldestMonth - 1]} – ${monthAbbr[newestMonth - 1]} $newestYear"
            else "${monthAbbr[oldestMonth - 1]} $oldestYear – ${monthAbbr[newestMonth - 1]} $newestYear"
        }

        ChartViewMode.Weekly -> {
            if (weeklyData.isEmpty()) return ""
            val newestEpoch = LocalDate.parse(weeklyData.first().weekStart).toEpochDays()
            val pageNewest = LocalDate.fromEpochDays(newestEpoch - offset * 12 * 7)
            val pageOldest = LocalDate.fromEpochDays(newestEpoch - (offset * 12 + 11) * 7)
            val pageNewestEnd = pageNewest.plus(6, DateTimeUnit.DAY)
            if (pageOldest.year == pageNewestEnd.year) "${monthAbbr[pageOldest.month.number - 1]} – ${monthAbbr[pageNewestEnd.month.number - 1]} ${pageNewestEnd.year}"
            else "${monthAbbr[pageOldest.month.number - 1]} ${pageOldest.year} – ${monthAbbr[pageNewestEnd.month.number - 1]} ${pageNewestEnd.year}"
        }
    }
}

internal fun monthlyMaxPageOffset(data: List<MonthlyChartPoint>): Int {
    if (data.size <= 1) return 0
    val (ny, nm) = data.first().period.split("-").let { it[0].toInt() to it[1].toInt() }
    val (oy, om) = data.last().period.split("-").let { it[0].toInt() to it[1].toInt() }
    return ((ny - oy) * 12 + (nm - om)) / 12
}

internal fun weeklyMaxPageOffset(data: List<WeeklyChartPoint>): Int {
    if (data.size <= 1) return 0
    val newest = LocalDate.parse(data.first().weekStart).toEpochDays()
    val oldest = LocalDate.parse(data.last().weekStart).toEpochDays()
    return (((newest - oldest) / 7) / 12).toInt()
}

internal fun niceMax(max: Int): Int {
    if (max == 0) return 100
    val step = when {
        max <= 100 -> 25
        max <= 300 -> 50
        max <= 600 -> 100
        else -> 200
    }
    return ((max + step - 1) / step) * step
}
